import { OrderType, type OrderTypeDto } from '@onrunning/backstage-models';
import { useSessionStorage, type RemovableRef } from '@vueuse/core';
import { defineStore } from 'pinia';

import type { Draft } from '@/types/draft.interface';
import type { EditableOrder, Order } from '@/types/order.interface';

interface State<T extends Draft | EditableOrder> {
  orderUpdated: RemovableRef<boolean>;
  activeOrderNumber: string | null;
  orders: Map<string, T | Order>;
  updatedOrders: RemovableRef<Map<string, T>>;
  orderTypeInfo: OrderTypeDto | null;
  orderType: OrderType | null;
}

const defaultState: State<Draft | EditableOrder> = {
  orders: new Map(),
  updatedOrders: useSessionStorage('order/updatedOrders', new Map()),
  orderUpdated: useSessionStorage('order/orderUpdated', false),
  activeOrderNumber: null,
  orderType: null,
  orderTypeInfo: null,
};

export const useOrderStore = defineStore('order', {
  state: (): State<Draft | EditableOrder> => ({ ...defaultState }),

  getters: {
    activeOrder: (state) => {
      return state.activeOrderNumber ? state.orders.get(state.activeOrderNumber) : null;
    },
  },

  actions: {
    setOrders(orders: Map<string, Draft | EditableOrder | Order>) {
      this.orders = orders;
      this.activeOrderNumber = this.orders.values().next().value?.orderNumber || null;
    },

    setActiveOrderNumber(orderNumber: string | null) {
      this.activeOrderNumber = orderNumber;
    },

    setOrder(order: Draft | EditableOrder) {
      this.orders.set(order.orderNumber, order);
    },

    setUpdatedOrder(order: Draft | EditableOrder) {
      this.updatedOrders.set(order.orderNumber, order);

      this.orderUpdated = true;
    },

    setOrderTypeInfo(orderTypeInfo: OrderTypeDto, orderType: OrderType) {
      this.orderTypeInfo = orderTypeInfo;
      this.orderType = orderType;
    },

    deleteOrder(orderNumber: string) {
      this.updatedOrders.delete(orderNumber);
      this.orders.delete(orderNumber);
    },

    setUpdateStatus(status: boolean) {
      this.orderUpdated = status;
    },

    reset() {
      this.orderUpdated = false;
      this.orderTypeInfo = null;
      this.orderType = null;
      this.activeOrderNumber = null;
      this.orders = new Map<string, Draft | EditableOrder>();
      this.updatedOrders = new Map<string, Draft | EditableOrder>();
    },
  },
});
